// src/components/InputForm.jsx
import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, useMediaQuery, useTheme } from '@mui/material';
import SalaryInput from './SalaryInput';
import OvertimeInput from './OvertimeInput';
import PensionInput from './PensionInput';
import StudentLoanInput from './StudentLoanInput';
import YearSelector from './YearSelector';
import ScottishResidentSelector from './ScottishResident';
import { lightColours } from '../../../../styles/colours';
import { trackEvent } from '../../../../analytics';
import OptionsToggleButton from './OptionsToggleButton';
import { useConfig } from '../../../../context/ConfigContext';

const InputForm = ({ onInputChange, config, initialSalary = 100000 }) => {
  const [salary, setSalary] = useState(initialSalary);
  const { taxYear } = useConfig();
  const year = taxYear;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Overtime state
  const [overtimeValue, setOvertimeValue] = useState(0);

  // Pension state
  const [pensionContribution, setPensionContribution] = useState(0);
  const [pensionInputType, setPensionInputType] = useState('percentage'); // 'percentage' or 'amount'
  const [pensionScheme, setPensionScheme] = useState('Auto-enrolment');
  const [pensionCalculationBasis, setPensionCalculationBasis] = useState('Qualifying Earnings');
  // New state for includeOvertime toggle in Pension
  const [pensionIncludeOvertime, setPensionIncludeOvertime] = useState(false);
  // 2029 Mode: Apply NI to salary sacrifice contributions above £2,000
  const [pension2029Mode, setPension2029Mode] = useState(false);

  // Student Loan state
  const [studentLoanPlan, setStudentLoanPlan] = useState('none');

  // Tabs state: 0 - Overtime, 1 - Pension, 2 - Student Loan
  const [activeTab, setActiveTab] = useState(0);

  // Update salary when initialSalary prop changes (for SEO pages)
  useEffect(() => {
    setSalary(initialSalary);
  }, [initialSalary]);

  // Show more options flag
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  // For Personal pensions, force calculation basis to "Gross Salary".
  useEffect(() => {
    if (pensionScheme === 'Personal') {
      setPensionCalculationBasis('Gross Salary');
    }
  }, [pensionScheme]);

  // Update parent component whenever any input changes.
  useEffect(() => {
    onInputChange({
      year,
      salary,
      overtime: {
        include: overtimeValue !== 0, // Only include if nonzero
        value: overtimeValue,
        inputType: 'amount',
      },
      pension: {
        active: pensionContribution !== 0, // Active if user enters a value
        contribution: pensionContribution,
        inputType: pensionInputType, // Now determined by the toggle
        scheme: pensionScheme,
        calculationBasis: pensionCalculationBasis,
        includeOvertime: pensionIncludeOvertime,
        enable2029Mode: pension2029Mode,
      },
      studentLoan: {
        plan: studentLoanPlan,
      },
    });
  }, [
    year,
    salary,
    overtimeValue,
    pensionContribution,
    pensionInputType,
    pensionScheme,
    pensionCalculationBasis,
    pensionIncludeOvertime,
    pension2029Mode,
    studentLoanPlan,
    onInputChange,
  ]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  
    // Map tab index to meaningful names
    const tabNames = ["Overtime", "Pension", "Student Loan"];
    const selectedTab = tabNames[newValue];
  
    // Track the tab click event
    trackEvent("Input Form Tabs", "Tab Click", selectedTab);
  };
  

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'center',
        width: '100%',
      }}
    >
      {/* Salary Input remains always visible */}
      <SalaryInput salary={salary} onSalaryChange={setSalary} />

      {/* Options container with a border and background */}
      <Box
        sx={{
          width: '100%',
          mt: 2,
          border: `1px solid ${lightColours.darkBlue}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Options header with toggle button */}
        <OptionsToggleButton showMoreOptions={showMoreOptions} setShowMoreOptions={setShowMoreOptions} />

        {/* Options content: tabs and their panel */}
        {showMoreOptions && (
          <Box sx={{ pt: 1, px: 3, pb: 3, backgroundColor: '#f9f9f9' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : 'off'}
            allowScrollButtonsMobile={isMobile}
            centered={!isMobile}
            textColor="primary"
            indicatorColor="primary"
            sx={{ mb: 2 }}
          >
            <Tab label="Tax Year" />
            <Tab label="Residency" />
            <Tab label="Overtime" />
            <Tab label="Pension" />
            <Tab label="Student Loan" />
          </Tabs>

            {activeTab === 0 && (
              <YearSelector />
            )}
            {activeTab === 1 && (
              <ScottishResidentSelector />
            )}
            {activeTab === 2 && (
              <OvertimeInput 
                overtimeValue={overtimeValue} 
                onOvertimeValueChange={setOvertimeValue} 
                salary={salary}
                config={config}
              />
            )}
            {activeTab === 3 && (
              <PensionInput
                pensionContribution={pensionContribution}
                pensionInputType={pensionInputType}
                pensionScheme={pensionScheme}
                pensionCalculationBasis={pensionCalculationBasis}
                onPensionContributionChange={setPensionContribution}
                onPensionInputTypeChange={setPensionInputType}
                onPensionSchemeChange={setPensionScheme}
                onPensionCalculationBasisChange={setPensionCalculationBasis}
                salary={salary}  // pass the user's salary here
                includeOvertime={pensionIncludeOvertime}
                onIncludeOvertimeChange={setPensionIncludeOvertime}
                enable2029Mode={pension2029Mode}
                onEnable2029ModeChange={setPension2029Mode}
                taxYear={taxYear}
              />
            )}
            {activeTab === 4 && (
              <StudentLoanInput
                studentLoanPlan={studentLoanPlan}
                onStudentLoanPlanChange={setStudentLoanPlan}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InputForm;
